import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { usePresence } from '../hooks/usePresence';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  arrayUnion,
  arrayRemove,
  onSnapshot,
  addDoc,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';
import QRCode from 'qrcode';
import { Html5Qrcode } from 'html5-qrcode';

interface PracticeBuddy {
  uid: string;
  name: string;
  nickname: string;
  instrument: string;
  practiceSessionDuration: number;
  dailyPracticeGoal: number;
  totalDailyPractice: number;
  totalWeeklyPractice: number;
  currentStreak: number;
  badgesEarned: number;
  linkedBuddiesCount: number;
  heartsGiven: number;
  heartsReceived: number;
  nudgesReceived: number;
  isOnline: boolean;
  lastSeen: any;
}

interface SocialPost {
  id: string;
  type: 'achievement' | 'streak' | 'goal' | 'badge';
  studentId: string;
  studentName: string;
  studentNickname: string;
  content: string;
  timestamp: any;
  hearts: string[];
  nudges: string[];
}

interface FeedEvent {
  id: string;
  type: 'heart_sent' | 'nudge_sent' | 'daily_goal_reached' | 'weekly_goal_reached' | 'badge_earned';
  fromUserId: string;
  fromUserNickname: string;
  toUserId?: string; // For heart/nudge events
  toUserNickname?: string; // For heart/nudge events
  badgeName?: string; // For badge events
  timestamp: any;
  hearts: string[]; // Users who sent hearts to this event
  nudges: string[]; // Users who sent nudges to this event
}


interface PracticeSession {
  id: string;
  studentId: string;
  lessonId: string;
  plannedTotalMinutes: number;
  plannedDurations: Record<string, number>;
  actualDurations: Record<string, number>;
  completed: boolean;
  startAt: any;
  endAt?: any;
  durationSec?: number;
  taskCounts: Record<string, number>;
  createdAt: any;
}

const Social: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [activeTab, setActiveTab] = useState<'buddies' | 'add-buddy'>('buddies');
  const [loading, setLoading] = useState(true);
  const [buddies, setBuddies] = useState<PracticeBuddy[]>([]);
  const [feedPosts, setFeedPosts] = useState<SocialPost[]>([]);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [buddySearchId, setBuddySearchId] = useState('');
  const [addingBuddy, setAddingBuddy] = useState(false);
  const [qrCodeScanned, setQrCodeScanned] = useState(false);
  const [qrCodeError, setQrCodeError] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [currentUserData, setCurrentUserData] = useState<any>(null);
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState<any>(null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerElementRef = useRef<HTMLDivElement>(null);
  const lastDetectionTime = useRef<number>(0);

  // Get buddy IDs for presence tracking - memoize to prevent infinite loops
  const buddyIds = useMemo(() => buddies.map(buddy => buddy.uid), [buddies]);
  const { presence, loading: presenceLoading } = usePresence(buddyIds);

  // Load initial data
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    loadUserData();
    loadBuddies();
    loadFeedPosts();
  }, [currentUser, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update buddy online status when presence data changes
  useEffect(() => {
    if (presence && Object.keys(presence).length > 0) {
      setBuddies(prevBuddies => 
        prevBuddies.map(buddy => {
          const presenceData = presence[buddy.uid];
          
          if (presenceData) {
            return {
              ...buddy,
              isOnline: presenceData.online || false,
              lastSeen: presenceData.lastSeen ? new Date(presenceData.lastSeen) : null
            };
          }
          return buddy;
        })
      );
    }
  }, [presence]);

  // Initialize scanner when scanning starts
  useEffect(() => {
    if (scanning && scannerElementRef.current) {
      const initializeScanner = async () => {
        try {
          // Clear any existing scanner
          if (scannerRef.current) {
            await scannerRef.current.clear();
          }

          // Create new scanner instance
          scannerRef.current = new Html5Qrcode("qr-reader");

          // Get available cameras
          const cameras = await Html5Qrcode.getCameras();
          if (cameras && cameras.length) {
            // Use the back camera if available, otherwise use the first camera
            const cameraId = cameras.find(camera => 
              camera.label.toLowerCase().includes('back') || 
              camera.label.toLowerCase().includes('rear')
            )?.id || cameras[0].id;

            // Start scanning with camera
            await scannerRef.current.start(
              cameraId,
              {
                fps: 10,
                qrbox: { width: 250, height: 250 },
                aspectRatio: 1.0
              },
              (decodedText, decodedResult) => {
                handleQRCodeDetected(decodedText);
              },
              (error) => {
                // Ignore common errors that don't need to be shown to user
                if (error && !error.includes('No MultiFormat Readers')) {
                  console.log('QR Code scan error:', error);
                }
              }
            );
          } else {
            throw new Error('No cameras found');
          }
        } catch (err: any) {
          console.error('Error initializing QR scanner:', err);
          setQrCodeError('Failed to start camera. Please check permissions.');
          setScanning(false);
        }
      };

      // Small delay to ensure the DOM element is rendered
      setTimeout(initializeScanner, 100);
    }
  }, [scanning]);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
    };
  }, []);


  // Real-time updates for online status
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (doc) => {
        if (doc.exists()) {
          setCurrentUserData(doc.data());
        }
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const loadUserData = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setCurrentUserData(userDoc.data());
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadBuddies = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();
      
      if (!userData?.linkedBuddies || userData.linkedBuddies.length === 0) {
        setBuddies([]);
        setLoading(false);
        return;
      }

      const buddyPromises = userData.linkedBuddies.map(async (buddyId: string) => {
        // Validate buddyId before using it in document path
        if (!buddyId || buddyId.includes('/') || buddyId.includes('//')) {
          console.warn(`Invalid buddy ID: ${buddyId}`);
          return null;
        }
        
        try {
          const buddyDoc = await getDoc(doc(db, 'users', buddyId));
          if (!buddyDoc.exists()) {
            console.warn(`Buddy document not found for ID: ${buddyId} (user may have been deleted)`);
            return null;
          }
          
          const buddyData = buddyDoc.data();
        
          // Calculate current streak
          const sessionsQuery = query(
            collection(db, 'practiceSessions'),
            where('studentId', '==', buddyId),
            where('completed', '==', true)
          );
          const sessionsSnapshot = await getDocs(sessionsQuery);
          const sessions: PracticeSession[] = sessionsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as PracticeSession[];
          
          const currentStreak = calculateCurrentStreak(sessions);
          
          // Calculate today's practice
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todaySessions = sessions.filter(session => {
            const sessionDate = session.endAt?.toDate?.() || new Date(session.startAt?.toDate?.() || 0);
            sessionDate.setHours(0, 0, 0, 0);
            return sessionDate.getTime() === today.getTime();
          });
          
          const totalDailyPractice = todaySessions.reduce((sum, session) => sum + (session.durationSec || 0), 0) / 60; // in minutes
          
          // Calculate weekly practice
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          const weeklySessions = sessions.filter(session => {
            const sessionDate = session.endAt?.toDate?.() || new Date(session.startAt?.toDate?.() || 0);
            return sessionDate >= weekAgo;
          });
          
          const totalWeeklyPractice = weeklySessions.reduce((sum, session) => sum + (session.durationSec || 0), 0) / 60; // in minutes
          
          // Load badges
          const { loadAllUserBadges } = await import('../utils/badgeCalculator');
          const badges = await loadAllUserBadges(buddyId, db);
          
          // Online status will be determined by presence data from Realtime Database
          // For now, set as offline - will be updated when presence data loads
          const isOnline = false;

          return {
            uid: buddyId,
            name: buddyData.name || 'Unknown',
            nickname: buddyData.nickname || buddyData.name || 'Unknown',
            instrument: buddyData.instrument || 'Unknown',
            practiceSessionDuration: buddyData.practiceSessionDuration || 30,
            dailyPracticeGoal: buddyData.practiceSessionDuration || 30,
            totalDailyPractice: Math.round(totalDailyPractice),
            totalWeeklyPractice: Math.round(totalWeeklyPractice),
            currentStreak,
            badgesEarned: badges.length,
            linkedBuddiesCount: buddyData.linkedBuddies?.length || 0,
            heartsGiven: buddyData.heartsGiven || 0,
            heartsReceived: buddyData.heartsReceived?.length || 0,
            nudgesReceived: buddyData.nudgesReceived?.length || 0,
            isOnline,
            lastSeen: buddyData.lastActiveAt
          };
        } catch (error) {
          console.warn(`Error fetching buddy document for ID: ${buddyId}:`, error);
          return null;
        }
      });

      const buddyResults = await Promise.all(buddyPromises);
      setBuddies(buddyResults.filter(buddy => buddy !== null) as PracticeBuddy[]);
    } catch (error) {
      console.error('Error loading buddies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFeedPosts = async () => {
    // This function is now replaced by loadFeedEvents
    // Keeping it for compatibility but not using it
    setFeedPosts([]);
  };

  // Create a feed event
  const createFeedEvent = async (eventData: Omit<FeedEvent, 'id' | 'timestamp' | 'hearts' | 'nudges'>) => {
    try {
      const eventRef = await addDoc(collection(db, 'feedEvents'), {
        ...eventData,
        timestamp: serverTimestamp(),
        hearts: [],
        nudges: []
      });
      return eventRef.id;
    } catch (error) {
      console.error('Error creating feed event:', error);
    }
  };


  const calculateCurrentStreak = (sessions: PracticeSession[]): number => {
    if (sessions.length === 0) return 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let streak = 0;
    let checkDate = new Date(today);
    
    const sortedSessions = sessions
      .filter(s => (s.durationSec || 0) >= 600) // 10+ minutes
      .sort((a, b) => {
        const aDate = a.endAt?.toDate?.() || new Date(a.endAt || 0);
        const bDate = b.endAt?.toDate?.() || new Date(b.endAt || 0);
        return bDate.getTime() - aDate.getTime();
      });
    
    for (const session of sortedSessions) {
      const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
      sessionDate.setHours(0, 0, 0, 0);
      
      if (sessionDate.getTime() === checkDate.getTime()) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else if (sessionDate.getTime() < checkDate.getTime()) {
        break;
      }
    }
    
    return streak;
  };

  const generateQRCode = async () => {
    if (!currentUser?.uid) return;
    
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(currentUser.uid, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(qrCodeDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const startScanning = () => {
    try {
      // Prevent multiple scanner instances
      if (scannerRef.current) {
        console.log('Scanner already running');
        return;
      }
      
      setQrCodeError('');
      setScanning(true);
      setShowQRScanner(true);
    } catch (err) {
      console.error('Error starting QR scanner:', err);
      setQrCodeError('Failed to start camera. Please check permissions.');
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    setScanning(false);
    setShowQRScanner(false);
    if (scannerRef.current) {
      try {
        await scannerRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
      scannerRef.current = null;
    }
  };

  const handleQRCodeDetected = async (data: string) => {
    try {
      // Debounce: prevent multiple rapid detections (within 2 seconds)
      const now = Date.now();
      if (now - lastDetectionTime.current < 2000) {
        console.log('QR detection debounced');
        return;
      }
      lastDetectionTime.current = now;
      
      setQrCodeError(''); // Clear any previous errors
      
      // Stop the scanner immediately to prevent multiple detections and jittering
      if (scannerRef.current) {
        try {
          await scannerRef.current.clear();
        } catch (err) {
          console.log('Scanner already stopped');
        }
        scannerRef.current = null;
      }
      
      setLoadingStudent(true);
      setShowQRScanner(false);
      setScanning(false);

      // Try to parse as JSON first (in case it's a full QR data object)
      let qrData;
      try {
        qrData = JSON.parse(data);
      } catch (err) {
        // If not JSON, treat as student ID directly
        qrData = {
          type: 'student-link',
          studentId: data
        };
      }

      // Validate the QR data
      if (qrData.type === 'student-link' && qrData.studentId) {
        // Try to fetch student name
        try {
          const studentDoc = await getDoc(doc(db, 'users', qrData.studentId));
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            setScannedData({
              type: 'student-link',
              studentId: qrData.studentId,
              studentName: studentData.name || studentData.nickname || 'Student'
            });
            setBuddySearchId(qrData.studentId);
            setQrCodeScanned(true);
          } else {
            setScannedData({
              type: 'student-link',
              studentId: qrData.studentId,
              studentName: 'Student (Profile not found)'
            });
            setBuddySearchId(qrData.studentId);
            setQrCodeScanned(true);
          }
        } catch (fetchErr) {
          console.warn('Could not fetch student details:', fetchErr);
          setScannedData({
            type: 'student-link',
            studentId: qrData.studentId,
            studentName: 'Student (Name will be loaded)'
          });
          setBuddySearchId(qrData.studentId);
          setQrCodeScanned(true);
        }
      } else {
        setQrCodeError('Invalid QR code format. Please scan a valid student QR code.');
      }
    } catch (error: any) {
      console.error('Error processing QR code:', error);
      setQrCodeError(`Error processing QR code: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingStudent(false);
    }
  };

  const addBuddy = async () => {
    if (!currentUser || !buddySearchId.trim()) return;
    
    try {
      setAddingBuddy(true);
      
      // Validate buddy ID before using it
      const buddyId = buddySearchId.trim();
      if (!buddyId || buddyId.includes('/') || buddyId.includes('//')) {
        setError('Invalid Student ID format. Please check the ID and try again.');
        return;
      }
      
      // Find user by ID
      const buddyDoc = await getDoc(doc(db, 'users', buddyId));
      if (!buddyDoc.exists()) {
        setError('Student not found. Please check the Student ID.');
        return;
      }
      
      const buddyData = buddyDoc.data();
      
      // Check if it's not the current user
      if (buddyDoc.id === currentUser.uid) {
        setError('You cannot add yourself as a buddy!');
        return;
      }
      
      // Check if already linked
      const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const currentUserData = currentUserDoc.data();
      
      if (currentUserData?.linkedBuddies?.includes(buddyId)) {
        setError('This student is already your buddy!');
        return;
      }
      
      // Add bidirectional connection with linking timestamp
      const linkingTimestamp = serverTimestamp();
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        linkedBuddies: arrayUnion(buddyId),
        buddyLinkedAt: {
          ...currentUserData?.buddyLinkedAt,
          [buddyId]: linkingTimestamp
        }
      });
      
      await updateDoc(doc(db, 'users', buddyId), {
        linkedBuddies: arrayUnion(currentUser.uid),
        buddyLinkedAt: {
          ...buddyData?.buddyLinkedAt,
          [currentUser.uid]: linkingTimestamp
        }
      });
      
      // Create welcome posts
      await addDoc(collection(db, 'socialPosts'), {
        type: 'achievement',
        studentId: currentUser.uid,
        studentName: currentUser.name,
        studentNickname: currentUser.nickname || currentUser.name,
        content: `🎉 Connected with ${buddyData.nickname || buddyData.name}! Let's practice together!`,
        timestamp: serverTimestamp(),
        hearts: [],
        nudges: []
      });
      
      await addDoc(collection(db, 'socialPosts'), {
        type: 'achievement',
        studentId: buddyId,
        studentName: buddyData.name,
        studentNickname: buddyData.nickname || buddyData.name,
        content: `🎉 Connected with ${currentUser.nickname || currentUser.name}! Let's practice together!`,
        timestamp: serverTimestamp(),
        hearts: [],
        nudges: []
      });
      
      setBuddySearchId('');
      await loadBuddies();
      await loadFeedPosts();
      
      setError(''); // Clear any previous errors
    } catch (error) {
      console.error('Error adding buddy:', error);
      setError('Error adding buddy. Please try again.');
    } finally {
      setAddingBuddy(false);
    }
  };


  const removeBuddy = async (buddyId: string) => {
    if (!currentUser) return;
    
    // Confirm removal
    const confirmed = window.confirm('Are you sure you want to remove this buddy? This will disconnect you from each other.');
    if (!confirmed) return;

    try {
      console.log('Removing buddy:', buddyId);
      
      // Remove buddy from current user's linkedBuddies
      await updateDoc(doc(db, 'users', currentUser.uid), {
        linkedBuddies: arrayRemove(buddyId)
      });
      console.log('Removed from current user');

      // Remove current user from buddy's linkedBuddies
      await updateDoc(doc(db, 'users', buddyId), {
        linkedBuddies: arrayRemove(currentUser.uid)
      });
      console.log('Removed from buddy');

      // Reload buddies list
      await loadBuddies();
      await loadFeedPosts();
      
      console.log('Buddy removed successfully');
      setError(''); // Clear any previous errors
    } catch (error) {
      console.error('Error removing buddy:', error);
      setError(`Failed to remove buddy: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return '';
    
    let date;
    try {
      date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'unknown time';
      }
    } catch (error) {
      return 'unknown time';
    }
    
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your social network...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 shadow-xl sticky top-0 z-10 overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-white/10 to-transparent rounded-full translate-y-12 -translate-x-12"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="text-4xl mr-4 animate-bounce">👥</div>
              <h1 className="text-3xl font-bold text-white">
                Practice Buddies
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 backdrop-blur-sm transform hover:scale-105"
              >
                Back to Dashboard
              </button>
              <button
                onClick={() => navigate('/feed')}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 backdrop-blur-sm transform hover:scale-105"
              >
                📰 Feed
              </button>
              <button
                onClick={() => setActiveTab('add-buddy')}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 backdrop-blur-sm transform hover:scale-105 ${
                  activeTab === 'add-buddy' 
                    ? 'bg-white/30 text-white shadow-lg' 
                    : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
              >
                ➕ Add Buddy
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Buddy Cards Section - Show when on buddies tab */}
        {activeTab === 'buddies' && (
          <div className="space-y-6 mb-8">
            {/* Hero Section for Buddies */}
            <div className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-3xl p-8 text-center shadow-xl border border-purple-200 overflow-hidden">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-200 to-rose-200 rounded-full translate-y-12 -translate-x-12 opacity-30"></div>
              
              <div className="relative">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="text-8xl animate-pulse">🎵</div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-400 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-purple-400 rounded-full animate-pulse"></div>
                  </div>
                </div>
                
                <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  Your Amazing Practice Buddies! 🌟
                </h2>
                <p className="text-xl text-gray-700 mb-6 max-w-2xl mx-auto">
                  Connect with friends, cheer each other on, and make practicing music even more fun together!
                </p>
                
                {/* Stats Cards */}
                <div className="flex justify-center space-x-8 mb-6">
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl px-6 py-4">
                    <div className="text-3xl font-bold text-blue-600">{buddies.length}</div>
                    <div className="text-sm font-medium text-gray-700">Practice Buddies</div>
                  </div>
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl px-6 py-4">
                    <div className="text-3xl font-bold text-purple-600">🎯</div>
                    <div className="text-sm font-medium text-gray-700">Motivate Together</div>
                  </div>
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl px-6 py-4">
                    <div className="text-3xl font-bold text-pink-600">💝</div>
                    <div className="text-sm font-medium text-gray-700">Send Hearts</div>
                  </div>
                </div>
              </div>
            </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
                <div className="ml-auto">
                  <button
                    onClick={() => setError('')}
                    className="text-red-400 hover:text-red-600"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{successMessage}</p>
                </div>
                <div className="ml-auto">
                  <button
                    onClick={() => setSuccessMessage('')}
                    className="text-green-400 hover:text-green-600"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {buddies.length === 0 ? (
            <div className="relative bg-gradient-to-br from-orange-50 via-yellow-50 to-pink-50 rounded-3xl p-12 text-center shadow-2xl border border-orange-200 overflow-hidden">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-200 to-yellow-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-200 to-rose-200 rounded-full translate-y-12 -translate-x-12 opacity-30"></div>
              
              <div className="relative">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="text-8xl animate-bounce">👥</div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-400 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                  </div>
                </div>
                
                <h3 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  Ready to Make Some Music Friends? 🚀
                </h3>
                <p className="text-lg text-gray-700 mb-8 max-w-md mx-auto">
                  Connect with friends who love music just like you! Practice together, cheer each other on, and make learning even more exciting!
                </p>
                
                <button
                  onClick={() => setActiveTab('add-buddy')}
                  className="group relative bg-gradient-to-r from-orange-500 via-yellow-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-10 py-5 rounded-2xl font-bold text-xl shadow-2xl transform hover:scale-110 transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-pink-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center">
                    <div className="text-2xl mr-3 group-hover:animate-bounce">➕</div>
                    <span>Add Your First Buddy!</span>
                  </div>
                </button>
                
                <div className="flex justify-center space-x-8 mt-8">
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl px-4 py-2">
                    <div className="text-2xl mb-1">💝</div>
                    <div className="text-sm font-medium text-gray-700">Send Hearts</div>
                  </div>
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl px-4 py-2">
                    <div className="text-2xl mb-1">📈</div>
                    <div className="text-sm font-medium text-gray-700">Track Progress</div>
                  </div>
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl px-4 py-2">
                    <div className="text-2xl mb-1">🎉</div>
                    <div className="text-sm font-medium text-gray-700">Celebrate Wins</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {buddies.map((buddy) => (
                <div key={buddy.uid} className="relative bg-gradient-to-br from-white via-blue-50 to-purple-50 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] border border-purple-100 overflow-hidden">
                  {/* Colored Header */}
                  <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 px-6 py-4 relative">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                          <span className="text-white font-bold text-lg">
                            {(buddy.nickname || buddy.name || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">
                            {buddy.nickname || buddy.name}
                          </h3>
                          <div className="text-sm text-white/80">
                            {buddy.isOnline ? (
                              <span className="flex items-center text-green-200">
                                <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                                Online
                              </span>
                            ) : (
                              <span className="text-white/70">
                                {buddy.lastSeen ? `Last seen ${formatTimestamp(buddy.lastSeen)}` : 'Last seen unknown'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Remove Buddy Button */}
                      <button
                        onClick={() => removeBuddy(buddy.uid)}
                        className="text-white/70 hover:text-red-300 transition-colors p-1 rounded-full hover:bg-white/10"
                        title="Remove Buddy"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Today's Practice</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatTime(buddy.totalDailyPractice)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Daily Goal</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatTime(buddy.dailyPracticeGoal)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Weekly Total</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatTime(buddy.totalWeeklyPractice)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Current Streak</span>
                      <span className="inline-flex px-3 py-1 text-xs font-semibold bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 rounded-full border border-orange-200">
                        🔥 {buddy.currentStreak} days
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Badges Earned</span>
                      <span className="inline-flex px-3 py-1 text-xs font-semibold bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 rounded-full border border-yellow-200">
                        🏅 {buddy.badgesEarned} badges
                      </span>
                    </div>
                  </div>

                  {/* Hearts and Nudges Counts */}
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-pink-50 rounded-lg p-3 text-center">
                        <div className="flex items-center justify-center space-x-2 mb-1">
                          <span className="text-xl">💖</span>
                          <span className="text-lg font-bold text-pink-700">{buddy.heartsReceived}</span>
                        </div>
                        <span className="text-xs text-pink-600 font-medium">Hearts Received</span>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <div className="flex items-center justify-center space-x-2 mb-1">
                          <span className="text-xl">🤗</span>
                          <span className="text-lg font-bold text-blue-700">{buddy.nudgesReceived}</span>
                        </div>
                        <span className="text-xs text-blue-600 font-medium">Nudges Received</span>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        )}


        {activeTab === 'add-buddy' && (
          <div className="max-w-4xl mx-auto">
            {/* Hero Section for Add Buddy */}
            <div className="relative bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-3xl p-8 text-center shadow-xl border border-green-200 overflow-hidden mb-8">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-200 to-emerald-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-teal-200 to-cyan-200 rounded-full translate-y-12 -translate-x-12 opacity-30"></div>
              
              <div className="relative">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="text-8xl animate-pulse">🌟</div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-emerald-400 rounded-full animate-pulse"></div>
                  </div>
                </div>
                
                <h2 className="text-4xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
                  Add a Practice Buddy! 🎵
                </h2>
                <p className="text-xl text-gray-700 mb-6 max-w-2xl mx-auto">
                  Connect with friends who love music! Scan their QR code or enter their student ID to start practicing together!
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 relative">
              {/* Close Button */}
              <button
                onClick={() => setActiveTab('buddies')}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                title="Close Add Buddy"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {/* QR Code Section */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Share Your Student ID</h3>
                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <div className="text-sm text-gray-600 mb-2">Your Student ID:</div>
                  <div className="text-2xl font-mono font-bold text-indigo-600 mb-4">
                    {currentUser?.uid}
                  </div>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={async () => {
                        if (!showQRCode) {
                          await generateQRCode();
                        }
                        setShowQRCode(!showQRCode);
                      }}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      {showQRCode ? 'Hide' : 'Show'} QR Code
                    </button>
                <button
                  onClick={() => {
                    if (showQRScanner) {
                      stopScanning();
                      setQrCodeScanned(false);
                    } else {
                      startScanning();
                    }
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  {showQRScanner ? 'Cancel' : 'Scan'} QR Code
                </button>
                  </div>
                </div>
                
                {showQRCode && (
                  <div className="bg-white rounded-lg p-4 shadow-inner">
                    <div className="text-center">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Your QR Code</h4>
                      {qrCodeDataUrl ? (
                        <div className="bg-gray-100 rounded-lg p-4 mb-4 inline-block">
                          <img 
                            src={qrCodeDataUrl} 
                            alt="Student ID QR Code" 
                            className="w-64 h-64 mx-auto"
                          />
                        </div>
                      ) : (
                        <div className="bg-gray-100 rounded-lg p-4 mb-4">
                          <div className="text-sm text-gray-600 mb-2">Generating QR Code...</div>
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                        </div>
                      )}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <div className="text-sm text-gray-600 mb-2">Student ID:</div>
                        <div className="text-sm font-mono font-bold text-indigo-600 break-all">
                          {currentUser?.uid}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">
                        Have a friend scan this QR code or enter the Student ID to add you as a buddy
                      </p>
                    </div>
                  </div>
                )}

                {showQRScanner && !scannedData && (
                  <div className="bg-white rounded-lg p-4 shadow-inner">
                    <div className="text-center mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Scan Friend's QR Code</h4>
                      <p className="text-sm text-gray-600">Point your camera at your friend's QR code</p>
                    </div>
                    <div className="text-center">
                      <div className="mb-6">
                        <div 
                          ref={scannerElementRef}
                          id="qr-reader"
                          className="w-full max-w-md mx-auto"
                        ></div>
                      </div>
                      <p className="text-gray-600 mb-4">Point your camera at your friend's QR code</p>
                      <button
                        onClick={stopScanning}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-medium"
                      >
                        Stop Scanning
                      </button>
                    </div>
                  </div>
                )}

                {loadingStudent && (
                  <div className="bg-white rounded-lg p-4 shadow-inner">
                    <div className="text-center">
                      <div className="mb-6">
                        <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Loading Student Details...</h4>
                      <p className="text-sm text-gray-600">Please wait while we fetch the student information</p>
                    </div>
                  </div>
                )}

                {scannedData && !loadingStudent && (
                  <div className="bg-white rounded-lg p-4 shadow-inner">
                    <div className="text-center">
                      <div className="mb-6">
                        <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Student Found!</h4>
                      <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <p className="text-sm text-gray-600">Student Name: <span className="font-medium">{scannedData.studentName || 'Unknown'}</span></p>
                        <p className="text-sm text-gray-600">Student ID: <span className="font-mono text-xs">{scannedData.studentId}</span></p>
                      </div>
                      
                      <div className="flex justify-center space-x-4">
                        <button
                          onClick={addBuddy}
                          disabled={addingBuddy}
                          className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-6 py-3 rounded-md font-medium"
                        >
                          {addingBuddy ? 'Adding Buddy...' : 'Add as Buddy'}
                        </button>
                        <button
                          onClick={() => {
                            setScannedData(null);
                            setQrCodeScanned(false);
                            setBuddySearchId('');
                          }}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-md font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Add Buddy by ID Section */}
              <div className="border-t pt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Buddy by Student ID</h3>
                
                {qrCodeScanned && !qrCodeError && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-800">
                          QR Code scanned successfully! Student ID detected.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {qrCodeError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-red-800">
                          {qrCodeError}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student ID
                    </label>
                    <input
                      type="text"
                      value={buddySearchId}
                      onChange={(e) => {
                        setBuddySearchId(e.target.value);
                        setQrCodeScanned(false);
                        setQrCodeError('');
                      }}
                      placeholder="Enter your friend's Student ID"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        qrCodeScanned ? 'border-green-300 bg-green-50' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <button
                    onClick={addBuddy}
                    disabled={addingBuddy || !buddySearchId.trim()}
                    className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                  >
                    {addingBuddy ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Adding Buddy...</span>
                      </>
                    ) : (
                      <>
                        <span>➕</span>
                        <span>Add Buddy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Social;
